import React, { useEffect, useState } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import styles from './EnrollmentFormModal.module.css';

const PREP_MODES = [
  { id: 'self', label: 'Self Study' },
  { id: 'offline', label: 'Offline Coaching' },
  { id: 'online', label: 'Online Coaching' },
  { id: 'prrepx', label: 'PrrepX Pathshala Fully' },
  { id: 'other', label: 'Other' },
];

const EMPTY = {
  fullName: '', className: '', preparingFor: '', motherName: '', fatherName: '',
  dob: '', phone: '', prepMode: '', coachingName: '', additional: '',
};

/**
 * Enrollment details form — shown the first time a user enrolls.
 * Saved to users/{uid}/enrollmentDetails; prefilled on later enrollments.
 */
const EnrollmentFormModal = ({ programId, programLabel, onDone, onClose }) => {
  const { currentUser, userData } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!currentUser) return;
    // Prefill from earlier enrollment details or profile data
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'users', currentUser.uid));
        const d = snap.data() || {};
        // Prefill: this program's saved details first, else the latest snapshot, else profile
        const all = d.enrollmentDetails || {};
        const prev = (programId && all[programId]) || all.latest || {};
        // Normalise any prefill value to one of the dropdown options
        const prepOptions = ['JEE', 'NEET', 'Board Exam', 'School', 'Olympiad'];
        const rawPrep = prev.preparingFor || d.preparingFor || programLabel || '';
        const prepMatch = prepOptions.find(o => rawPrep.toLowerCase().includes(o.toLowerCase()))
          || (rawPrep.toLowerCase().includes('jee') || rawPrep.toLowerCase().includes('iit') ? 'JEE' : '')
          || (rawPrep.toLowerCase().includes('board') ? 'Board Exam' : '');
        setForm({
          fullName: prev.fullName || d.username || currentUser.displayName || '',
          className: prev.className || d.class || '',
          preparingFor: prepMatch,
          motherName: prev.motherName || '',
          fatherName: prev.fatherName || '',
          dob: prev.dob || '',
          phone: prev.phone || d.mobile || '',
          prepMode: prev.prepMode || '',
          coachingName: prev.coachingName || '',
          additional: prev.additional || '',
        });
      } catch { /* offline — start blank */ }
    })();
  }, [currentUser, userData, programLabel]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const needsCoaching = form.prepMode === 'offline' || form.prepMode === 'online';

  const validate = () => {
    const e = {};
    if (!form.fullName.trim()) e.fullName = 'Required';
    if (!form.className.trim()) e.className = 'Required';
    if (!form.preparingFor.trim()) e.preparingFor = 'Required';
    if (!form.motherName.trim()) e.motherName = 'Required';
    if (!form.fatherName.trim()) e.fatherName = 'Required';
    if (!form.dob) e.dob = 'Required';
    if (!/^\d{10}$/.test(form.phone.replace(/\D/g, '').slice(-10))) e.phone = 'Enter a valid 10-digit number';
    if (!form.prepMode) e.prepMode = 'Required';
    if (needsCoaching && !form.coachingName.trim()) e.coachingName = 'Which coaching?';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate() || !currentUser) return;
    setSaving(true);
    try {
      await setDoc(doc(db, 'users', currentUser.uid), {
        // Per-program map so multiple enrollments never overwrite each other
        enrollmentDetails: {
          [programId]: { ...form, programId, programLabel, enrolledAt: Date.now() },
          latest: { ...form, programId, programLabel, updatedAt: Date.now() },
        },
        // keep top-level profile fields in sync where they overlap
        username: form.fullName,
        class: form.className,
        preparingFor: form.preparingFor,
        mobile: form.phone,
      }, { merge: true });
      onDone();
    } catch {
      setSaving(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <div className={styles.header}>
          <h3 className={styles.title}>Enroll in {programLabel} <span className={styles.freeTag}>₹0 · 100% Free</span></h3>
          <p className={styles.subtitle}>Tell us about yourself — this helps us personalise your journey.</p>
          <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">✕</button>
        </div>

        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <div className={styles.grid}>
            <label className={styles.field}>
              <span>Full Name *</span>
              <input value={form.fullName} onChange={set('fullName')} placeholder="Your full name" />
              {errors.fullName && <em>{errors.fullName}</em>}
            </label>
            <label className={styles.field}>
              <span>Class *</span>
              <select value={form.className} onChange={set('className')}>
                <option value="">Select class</option>
                {['6','7','8','9','10','11','12','Dropper'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              {errors.className && <em>{errors.className}</em>}
            </label>
            <label className={styles.field}>
              <span>Preparing For *</span>
              <select value={form.preparingFor} onChange={set('preparingFor')}>
                <option value="">Select goal</option>
                {['JEE','NEET','Board Exam','School','Olympiad'].map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              {errors.preparingFor && <em>{errors.preparingFor}</em>}
            </label>
            <label className={styles.field}>
              <span>Phone Number *</span>
              <input value={form.phone} onChange={set('phone')} placeholder="10-digit mobile" inputMode="tel" />
              {errors.phone && <em>{errors.phone}</em>}
            </label>
            <label className={styles.field}>
              <span>Mother's Name *</span>
              <input value={form.motherName} onChange={set('motherName')} placeholder="Mother's name" />
              {errors.motherName && <em>{errors.motherName}</em>}
            </label>
            <label className={styles.field}>
              <span>Father's Name *</span>
              <input value={form.fatherName} onChange={set('fatherName')} placeholder="Father's name" />
              {errors.fatherName && <em>{errors.fatherName}</em>}
            </label>
            <label className={styles.field}>
              <span>Date of Birth *</span>
              <input type="date" value={form.dob} onChange={set('dob')} />
              {errors.dob && <em>{errors.dob}</em>}
            </label>
            <div className={styles.field}>
              <span>How are you preparing? *</span>
              <div className={styles.prepModes}>
                {PREP_MODES.map(m => (
                  <button
                    type="button"
                    key={m.id}
                    className={`${styles.prepChip} ${form.prepMode === m.id ? styles.prepChipActive : ''}`}
                    onClick={() => setForm(f => ({ ...f, prepMode: m.id, coachingName: (m.id === 'offline' || m.id === 'online') ? f.coachingName : '' }))}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
              {errors.prepMode && <em>{errors.prepMode}</em>}
            </div>
            {needsCoaching && (
              <label className={`${styles.field} ${styles.fullSpan}`}>
                <span>Which coaching institute? *</span>
                <input value={form.coachingName} onChange={set('coachingName')} placeholder="Coaching name" />
                {errors.coachingName && <em>{errors.coachingName}</em>}
              </label>
            )}
            <label className={`${styles.field} ${styles.fullSpan}`}>
              <span>Anything else we should know? (optional)</span>
              <textarea rows={2} value={form.additional} onChange={set('additional')} placeholder="Goals, school, health considerations…" />
            </label>
          </div>

          <button type="submit" className={styles.submitBtn} disabled={saving}>
            {saving ? 'Enrolling…' : `Enroll in ${programLabel} →`}
          </button>
        </form>
      </div>
    </div>
  );
};

export default EnrollmentFormModal;
